import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Col, Row } from 'reactstrap';

import FormModel from '../../../../models/Form';
import { modelOf } from '../../../../prop-types';
import AccountStore from '../../../../store/AccountStore';
import CheckoutStore from '../../../../store/CheckoutStore';
import FormFieldId from '../../../../types/FormFieldId';
import FormFieldType from '../../../../types/FormFieldType';
import { fieldTypeLabel, labels } from '../../../../util/formLabels';
import { normalizeBooleanField } from '../../../../util/checkoutFormUtils';
import FormGroupField from '../../../form/FormGroupField';
import SelectField from '../../../form/SelectField';
import Switch from '../../../common/Switch';
import globalTranslations from '../../../../i18n/globalTranslations';

const getBillingFormLabels = () => {
  return {
    [FormFieldId.BILLING_COMPANY]: labels.company,
    [FormFieldId.BILLING_VAT_NUMBER]: labels.businessId,
    [FormFieldId.BILLING_FIRST_NAME]: labels.firstName,
    [FormFieldId.BILLING_LAST_NAME]: labels.lastName,
    [FormFieldId.BILLING_STREET_ADDRESS]: labels.streetAddress,
    [FormFieldId.BILLING_POSTAL_CODE]: labels.postalCode,
    [FormFieldId.BILLING_CITY]: labels.city,
    [FormFieldId.BILLING_COUNTRY]: labels.country,
    [FormFieldId.BILLING_EMAIL]: labels.email,
    [FormFieldId.BILLING_PHONE]: labels.telephone,
  };
};

const billingFieldLabels = {
  [FormFieldId.BILLING_COMPANY]: fieldTypeLabel.COMPANY,
  [FormFieldId.BILLING_VAT_NUMBER]: fieldTypeLabel.VAT_NUMBER,
  [FormFieldId.BILLING_FIRST_NAME]: fieldTypeLabel.FIRSTNAME,
  [FormFieldId.BILLING_LAST_NAME]: fieldTypeLabel.LASTNAME,
  [FormFieldId.BILLING_STREET_ADDRESS]: fieldTypeLabel.STREET_ADDRESS,
  [FormFieldId.BILLING_POSTAL_CODE]: fieldTypeLabel.POSTAL_CODE,
  [FormFieldId.BILLING_CITY]: fieldTypeLabel.CITY,
  [FormFieldId.BILLING_COUNTRY]: fieldTypeLabel.COUNTRY,
  [FormFieldId.BILLING_EMAIL]: fieldTypeLabel.EMAIL,
  [FormFieldId.BILLING_PHONE]: fieldTypeLabel.PHONE,
  [FormFieldId.REGISTER_VISITOR_CUSTOMER]:
    fieldTypeLabel.REGISTER_VISITOR_CUSTOMER,
};

const validPickupPointFields = new Set([
  FormFieldId.BILLING_POSTAL_CODE,
  FormFieldId.BILLING_STREET_ADDRESS,
  FormFieldId.BILLING_CITY,
]);

// Layout order for flex wrap: name row, then street, then postal+city+phone, then rest. No gaps.
const BILLING_LAYOUT_ORDER = [
  FormFieldId.BILLING_COMPANY,
  FormFieldId.BILLING_VAT_NUMBER,
  FormFieldId.BILLING_FIRST_NAME,
  FormFieldId.BILLING_LAST_NAME,
  FormFieldId.BILLING_STREET_ADDRESS,
  FormFieldId.BILLING_POSTAL_CODE,
  FormFieldId.BILLING_CITY,
  FormFieldId.BILLING_PHONE,
  FormFieldId.BILLING_COUNTRY,
  FormFieldId.BILLING_EMAIL,
];

const BILLING_FIELD_WIDTH_CLASS = {
  [FormFieldId.BILLING_STREET_ADDRESS]: 'full',
  [FormFieldId.BILLING_EMAIL]: 'full',
};

const CheckoutBillingForm = ({
  accountStore,
  checkoutStore,
  onOrderUpdate,
  billingForm,
  hideSeparateDeliveryToggle,
}) => {
  const { cancelOrder, shippingInfoIsLoading, order } = checkoutStore;
  const { getCustomerField } = order;

  const disabled = shippingInfoIsLoading;

  const isBillingCompanyFormField = billingForm.getField(
    FormFieldId.BILLING_IS_COMPANY
  );

  const separateDeliveryField = getCustomerField(
    FormFieldId.SEPARATE_DELIVERY_ADDRESS
  ).field;

  const isSeparateDeliveryAddressFormField = billingForm.getField(
    FormFieldId.SEPARATE_DELIVERY_ADDRESS
  );

  const isCheckedSeparateDelivery = normalizeBooleanField(
    separateDeliveryField?.value
  );

  const handleSeparateDeliveryChange = (e) => {
    const newChecked = !!e.target.checked;
    if (separateDeliveryField) {
      separateDeliveryField.setValue(newChecked);
    }
    if (isSeparateDeliveryAddressFormField) {
      isSeparateDeliveryAddressFormField.setValue(newChecked ? '1' : null);
    }
    onOrderUpdate({ withoutTimer: true });
  };

  const updateOrder = ({ fetchPickupPoints }) => {
    onOrderUpdate({ withoutTimer: true, fetchPickupPoints });
  };

  const handleCustomerFieldChange = (e, fieldId) => {
    cancelOrder();
    const field = getCustomerField(fieldId).field;
    const rawValue = e.target.value;

    field.setValue(rawValue === '' ? null : rawValue);
  };

  const handleBlur = (_, field) => {
    billingForm.validateField(field.id);

    // TODO This should be improved when there is better time
    // User has no input, so no update is needed
    if (
      (field.value == null || field.value === '') &&
      field.value === field.previousValue
    ) {
      return;
    }
    // User has no input and the previousValue is uninitialized
    if (field.value !== field.previousValue && field.previousValue == null) {
      field.updatePreviousValue();
      return;
    }

    if (validPickupPointFields.has(field.id)) {
      updateOrder({ fetchPickupPoints: field.valid });
      return;
    }

    field.updatePreviousValue();
    updateOrder({ fetchPickupPoints: false });
  };

  const updateCustomerType = (checked) => {
    const booleanValue = !!checked;
    isBillingCompanyFormField.setValue(booleanValue);
    const customerField = getCustomerField(
      FormFieldId.BILLING_IS_COMPANY
    ).field;
    if (customerField) {
      customerField.setValue(booleanValue);
    }
    onOrderUpdate({ withoutTimer: true });
  };

  const renderIsBillingCompany = () => {
    if (!isBillingCompanyFormField) {
      return null;
    }
    const isCompany =
      isBillingCompanyFormField.value === true ||
      isBillingCompanyFormField.value === 1 ||
      isBillingCompanyFormField.value === '1' ||
      isBillingCompanyFormField.value === 'true';

    return (
      <Row>
        <Col>
          <div className="CheckoutBillingForm__toggle-row">
            <Switch
              checked={isCompany}
              onChange={(e) => updateCustomerType(e.target.checked)}
              size="xs"
              disabled={disabled}
            >
              <FormattedMessage {...globalTranslations.billingIsCompanyLabel} />
            </Switch>
          </div>
        </Col>
      </Row>
    );
  };

  const isCompany =
    isBillingCompanyFormField &&
    (isBillingCompanyFormField.value === true ||
      isBillingCompanyFormField.value === 1 ||
      isBillingCompanyFormField.value === '1' ||
      isBillingCompanyFormField.value === 'true');

  // When BILLING_IS_COMPANY exists, hide company/VAT unless in company mode. When it's not in the API, the field list defines what to render (show all billing fields).
  const hasBillingIsCompanyField = !!isBillingCompanyFormField;
  const companyOnlyFieldIds = new Set([
    FormFieldId.BILLING_COMPANY,
    FormFieldId.BILLING_VAT_NUMBER,
  ]);
  const fieldsToRender = hasBillingIsCompanyField
    ? order.billingFields.filter(
        (orderField) => isCompany || !companyOnlyFieldIds.has(orderField.id)
      )
    : order.billingFields;
  const renderedIdsFromApi = new Set(fieldsToRender.map((f) => f.id));
  // Form may have billing fields not in order.billingFields (e.g. BILLING_COUNTRY SELECT); exclude company/VAT when not in company mode (only when is_company field exists)
  const formOnlyBillingFields = billingForm
    .getFormFields(new Set([FormFieldType.BOOLEAN, FormFieldType.BUTTON]))
    .filter(
      (field) =>
        field.id.startsWith('BILLING_') &&
        !renderedIdsFromApi.has(field.id) &&
        (isCompany || !companyOnlyFieldIds.has(field.id))
    );

  const billingFormLabels = getBillingFormLabels();

  const renderedBillingIds = new Set([
    ...fieldsToRender.map((f) => f.id),
    ...formOnlyBillingFields.map((f) => f.id),
  ]);

  const renderField = (field, widthClass) => {
    if (!field.active) return null;
    if (!accountStore.loggedIn && field.type === FormFieldType.EMAIL) {
      return null;
    }
    const fieldId = field.id;
    const wrapperClass = `CheckoutBillingForm__field CheckoutBillingForm__field--${widthClass}`;

    if (field.type === FormFieldType.SELECT) {
      const options = (field.options || []).map((o) => ({
        value: String(o.value),
        label: o.label,
      }));

      return (
        <div className={wrapperClass} key={fieldId}>
          <SelectField
            field={field}
            fieldName={fieldId}
            formName={'checkout'}
            floatingLabel
            label={billingFormLabels[fieldId]}
            options={options}
            required={field.required}
            onChange={(e) => handleCustomerFieldChange(e, fieldId)}
            onBlur={handleBlur}
            onFocus={cancelOrder}
          />
        </div>
      );
    }

    let type;
    if (field.type === FormFieldType.PHONE) {
      type = 'tel';
    } else {
      type = field.type.toLowerCase();
    }

    return (
      <div className={wrapperClass} key={fieldId}>
        <FormGroupField
          field={field}
          fieldName={billingFieldLabels[fieldId]}
          formName={'checkout'}
          floatingLabel
          label={billingFormLabels[fieldId]}
          required={field.required}
          maxLength={field.max_length}
          minLength={field.min_length}
          type={type}
          onChange={(e) => handleCustomerFieldChange(e, fieldId)}
          onBlur={handleBlur}
          onFocus={cancelOrder}
        />
      </div>
    );
  };

  const billingNodes = (() => {
    const nodes = [];
    let skipNextVat = false;
    let skipNextLastName = false;
    let skipNextCity = false;
    let skipNextPhone = false;
    let skipNextCountry = false;
    for (const fieldId of BILLING_LAYOUT_ORDER) {
      if (!renderedBillingIds.has(fieldId)) continue;
      if (fieldId === FormFieldId.BILLING_VAT_NUMBER && skipNextVat) {
        skipNextVat = false;
        continue;
      }
      if (fieldId === FormFieldId.BILLING_LAST_NAME && skipNextLastName) {
        skipNextLastName = false;
        continue;
      }
      if (fieldId === FormFieldId.BILLING_CITY && skipNextCity) {
        skipNextCity = false;
        continue;
      }
      if (fieldId === FormFieldId.BILLING_PHONE && skipNextPhone) {
        skipNextPhone = false;
        continue;
      }
      if (fieldId === FormFieldId.BILLING_COUNTRY && skipNextCountry) {
        skipNextCountry = false;
        continue;
      }
      if (
        fieldId === FormFieldId.BILLING_COMPANY &&
        renderedBillingIds.has(FormFieldId.BILLING_VAT_NUMBER)
      ) {
        nodes.push({ type: 'company-row' });
        skipNextVat = true;
        continue;
      }
      if (fieldId === FormFieldId.BILLING_FIRST_NAME) {
        if (
          renderedBillingIds.has(FormFieldId.BILLING_LAST_NAME) &&
          renderedBillingIds.has(FormFieldId.BILLING_PHONE)
        ) {
          nodes.push({ type: 'name-row' });
          skipNextLastName = true;
          skipNextPhone = true;
          continue;
        }
      }
      if (
        fieldId === FormFieldId.BILLING_POSTAL_CODE &&
        renderedBillingIds.has(FormFieldId.BILLING_CITY)
      ) {
        nodes.push({ type: 'postal-row' });
        skipNextCity = true;
        if (renderedBillingIds.has(FormFieldId.BILLING_COUNTRY)) {
          skipNextCountry = true;
        }
        continue;
      }
      nodes.push({ type: 'field', fieldId });
    }
    return nodes;
  })();

  const renderCompanyRow = () => {
    const companyField = billingForm.getField(FormFieldId.BILLING_COMPANY);
    const vatField = billingForm.getField(FormFieldId.BILLING_VAT_NUMBER);
    if (!companyField?.active && !vatField?.active) return null;
    return (
      <div
        className="CheckoutBillingForm__field-row CheckoutBillingForm__field-row--company"
        key="billing-company-row"
      >
        {companyField?.active && (
          <div className="CheckoutBillingForm__field-row__company">
            <FormGroupField
              field={companyField}
              fieldName={billingFieldLabels[FormFieldId.BILLING_COMPANY]}
              formName={'checkout'}
              floatingLabel
              label={billingFormLabels[FormFieldId.BILLING_COMPANY]}
              required={companyField.required}
              maxLength={companyField.max_length}
              minLength={companyField.min_length}
              type="text"
              onChange={(e) =>
                handleCustomerFieldChange(e, FormFieldId.BILLING_COMPANY)
              }
              onBlur={handleBlur}
              onFocus={cancelOrder}
            />
          </div>
        )}
        {vatField?.active && (
          <div className="CheckoutBillingForm__field-row__vat">
            <FormGroupField
              field={vatField}
              fieldName={billingFieldLabels[FormFieldId.BILLING_VAT_NUMBER]}
              formName={'checkout'}
              floatingLabel
              label={billingFormLabels[FormFieldId.BILLING_VAT_NUMBER]}
              required={vatField.required}
              maxLength={vatField.max_length}
              minLength={vatField.min_length}
              type="text"
              onChange={(e) =>
                handleCustomerFieldChange(e, FormFieldId.BILLING_VAT_NUMBER)
              }
              onBlur={handleBlur}
              onFocus={cancelOrder}
            />
          </div>
        )}
      </div>
    );
  };

  const renderNameRow = () => {
    const firstNameField = billingForm.getField(FormFieldId.BILLING_FIRST_NAME);
    const lastNameField = billingForm.getField(FormFieldId.BILLING_LAST_NAME);
    const phoneField = billingForm.getField(FormFieldId.BILLING_PHONE);
    if (
      !firstNameField?.active &&
      !lastNameField?.active &&
      !phoneField?.active
    )
      return null;
    return (
      <div
        className="CheckoutBillingForm__field-row CheckoutBillingForm__field-row--name"
        key="billing-name-row"
      >
        {firstNameField?.active && (
          <div className="CheckoutBillingForm__field-row__first-name">
            <FormGroupField
              field={firstNameField}
              fieldName={billingFieldLabels[FormFieldId.BILLING_FIRST_NAME]}
              formName={'checkout'}
              floatingLabel
              label={labels.firstName}
              required={firstNameField.required}
              maxLength={firstNameField.max_length}
              minLength={firstNameField.min_length}
              type="text"
              onChange={(e) =>
                handleCustomerFieldChange(e, FormFieldId.BILLING_FIRST_NAME)
              }
              onBlur={handleBlur}
              onFocus={cancelOrder}
            />
          </div>
        )}
        {lastNameField?.active && (
          <div className="CheckoutBillingForm__field-row__last-name">
            <FormGroupField
              field={lastNameField}
              fieldName={billingFieldLabels[FormFieldId.BILLING_LAST_NAME]}
              formName={'checkout'}
              floatingLabel
              label={billingFormLabels[FormFieldId.BILLING_LAST_NAME]}
              required={lastNameField.required}
              maxLength={lastNameField.max_length}
              minLength={lastNameField.min_length}
              type="text"
              onChange={(e) =>
                handleCustomerFieldChange(e, FormFieldId.BILLING_LAST_NAME)
              }
              onBlur={handleBlur}
              onFocus={cancelOrder}
            />
          </div>
        )}
        {phoneField?.active && (
          <div className="CheckoutBillingForm__field-row__phone">
            <FormGroupField
              field={phoneField}
              fieldName={billingFieldLabels[FormFieldId.BILLING_PHONE]}
              formName={'checkout'}
              floatingLabel
              label={labels.telephone}
              required={phoneField.required}
              maxLength={phoneField.max_length}
              minLength={phoneField.min_length}
              type="tel"
              onChange={(e) =>
                handleCustomerFieldChange(e, FormFieldId.BILLING_PHONE)
              }
              onBlur={handleBlur}
              onFocus={cancelOrder}
            />
          </div>
        )}
      </div>
    );
  };

  const renderPostalRow = () => {
    const postalField = billingForm.getField(FormFieldId.BILLING_POSTAL_CODE);
    const cityField = billingForm.getField(FormFieldId.BILLING_CITY);
    const countryField = billingForm.getField(FormFieldId.BILLING_COUNTRY);
    if (!postalField?.active && !cityField?.active && !countryField?.active)
      return null;
    const hasCountry = countryField?.active;
    return (
      <div
        className={classNames(
          'CheckoutBillingForm__field-row',
          'CheckoutBillingForm__field-row--postal',
          {
            'CheckoutBillingForm__field-row--postal-no-country': !hasCountry,
          }
        )}
        key="billing-postal-row"
      >
        {postalField?.active && (
          <div className="CheckoutBillingForm__field-row__postal-code">
            <FormGroupField
              field={postalField}
              fieldName={billingFieldLabels[FormFieldId.BILLING_POSTAL_CODE]}
              formName={'checkout'}
              floatingLabel
              label={billingFormLabels[FormFieldId.BILLING_POSTAL_CODE]}
              required={postalField.required}
              maxLength={postalField.max_length}
              minLength={postalField.min_length}
              type="text"
              onChange={(e) =>
                handleCustomerFieldChange(e, FormFieldId.BILLING_POSTAL_CODE)
              }
              onBlur={handleBlur}
              onFocus={cancelOrder}
            />
          </div>
        )}
        {cityField?.active && (
          <div className="CheckoutBillingForm__field-row__city">
            <FormGroupField
              field={cityField}
              fieldName={billingFieldLabels[FormFieldId.BILLING_CITY]}
              formName={'checkout'}
              floatingLabel
              label={billingFormLabels[FormFieldId.BILLING_CITY]}
              required={cityField.required}
              maxLength={cityField.max_length}
              minLength={cityField.min_length}
              type="text"
              onChange={(e) =>
                handleCustomerFieldChange(e, FormFieldId.BILLING_CITY)
              }
              onBlur={handleBlur}
              onFocus={cancelOrder}
            />
          </div>
        )}
        {countryField?.active &&
          (countryField.type === FormFieldType.SELECT ? (
            <div className="CheckoutBillingForm__field-row__country">
              <SelectField
                field={countryField}
                fieldName={FormFieldId.BILLING_COUNTRY}
                formName={'checkout'}
                floatingLabel
                label={billingFormLabels[FormFieldId.BILLING_COUNTRY]}
                options={(countryField.options || []).map((o) => ({
                  value: String(o.value),
                  label: o.label,
                }))}
                required={countryField.required}
                onChange={(e) =>
                  handleCustomerFieldChange(e, FormFieldId.BILLING_COUNTRY)
                }
                onBlur={handleBlur}
                onFocus={cancelOrder}
              />
            </div>
          ) : (
            <div className="CheckoutBillingForm__field-row__country">
              <FormGroupField
                field={countryField}
                fieldName={billingFieldLabels[FormFieldId.BILLING_COUNTRY]}
                formName={'checkout'}
                floatingLabel
                label={billingFormLabels[FormFieldId.BILLING_COUNTRY]}
                required={countryField.required}
                maxLength={countryField.max_length}
                minLength={countryField.min_length}
                type="text"
                onChange={(e) =>
                  handleCustomerFieldChange(e, FormFieldId.BILLING_COUNTRY)
                }
                onBlur={handleBlur}
                onFocus={cancelOrder}
              />
            </div>
          ))}
      </div>
    );
  };

  return (
    <div className="CheckoutBillingForm">
      {renderIsBillingCompany()}
      <div className="CheckoutBillingForm__fields">
        {billingNodes.map((node) => {
          if (node.type === 'company-row') {
            return (
              <React.Fragment key="billing-company-row">
                {renderCompanyRow()}
              </React.Fragment>
            );
          }
          if (node.type === 'name-row') {
            const nameRow = renderNameRow();
            if (!nameRow) return null;
            if (isCompany) {
              return (
                <div
                  className="CheckoutBillingForm__contact-section"
                  key="billing-name-row"
                >
                  <div className="CheckoutBillingForm__contact-heading">
                    <FormattedMessage
                      {...globalTranslations.contactSectionTitle}
                    />
                  </div>
                  {nameRow}
                </div>
              );
            }
            return (
              <React.Fragment key="billing-name-row">{nameRow}</React.Fragment>
            );
          }
          if (node.type === 'postal-row') {
            return (
              <React.Fragment key="billing-postal-row">
                {renderPostalRow()}
              </React.Fragment>
            );
          }
          const field = billingForm.getField(node.fieldId);
          if (!field) return null;
          const widthClass = BILLING_FIELD_WIDTH_CLASS[node.fieldId] || 'full';
          return renderField(field, widthClass);
        })}
        {[...renderedBillingIds]
          .filter((id) => !BILLING_LAYOUT_ORDER.includes(id))
          .map((fieldId) => {
            const field = billingForm.getField(fieldId);
            if (!field) return null;
            return renderField(field, 'full');
          })}
      </div>
      {separateDeliveryField && !hideSeparateDeliveryToggle && (
        <Row className="CheckoutBillingForm__separate-delivery-row">
          <Col xs={12}>
            <div className="CheckoutBillingForm__toggle-row">
              <Switch
                checked={isCheckedSeparateDelivery}
                onChange={handleSeparateDeliveryChange}
                size="xs"
                disabled={disabled}
              >
                {labels.useDeliveryAddress}
              </Switch>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

CheckoutBillingForm.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  checkoutStore: modelOf(CheckoutStore).isRequired,
  billingForm: modelOf(FormModel).isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
  hideSeparateDeliveryToggle: PropTypes.bool,
};

CheckoutBillingForm.defaultProps = {
  hideSeparateDeliveryToggle: false,
};

export default inject(
  'accountStore',
  'checkoutStore'
)(observer(CheckoutBillingForm));
